import type React from 'react';
import { useCallback, useState } from 'react';

import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';

import AccountCircle from '@mui/icons-material/AccountCircle';
import CopyrightIcon from '@mui/icons-material/Copyright';
import MenuIcon from '@mui/icons-material/Menu';
import PolicyIcon from '@mui/icons-material/Policy';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFragment } from 'react-relay';
import { HeaderButtons, HeaderDDMenu, HeaderLogoLink } from './utils';

import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import { faParseIconName } from '../../inputs/IconAutocomplete';
import { Header_ZnglyInfo_Desktop } from './Header.desktop';
import type {
  Header_ZnglyInfo_Desktop$data,
  Header_ZnglyInfo_Desktop$key,
} from './__generated__/Header_ZnglyInfo_Desktop.graphql';

import { getMediaItemUrl } from '@/Components/posts/utils';
import { useAuthStore } from '@/Utility/auth/Auth';
import { Link as TsrLink, useNavigate } from '@tanstack/react-router';

import { MediaItemSizeEnum } from '@/graphql/types';
import Suspense from '../../Suspense';

import styles from './styles.module.css';

const drawerWidth = 240;

type Props = {
  znglyInfo: Header_ZnglyInfo_Desktop$data['znglyInfo'];
  style?: React.CSSProperties;
};

const DrawerContent = ({ znglyInfo }: Props) => {
  const user = useAuthStore((state) => state.user.user);

  const navigate = useNavigate();

  return (
    <Stack
      className={styles.drawer}
      direction="column"
      justifyContent="space-between"
      style={{ height: '100%', overflowY: 'auto' }}
    >
      <Stack>
        {/* Top Image */}
        <Toolbar sx={{ justifyContent: 'center', padding: 2, backgroundColor: 'var(--header-bg-col)' }}>
          <HeaderLogoLink znglyInfo={znglyInfo}>
            <Box
              component="img"
              style={{ width: drawerWidth - 90 }}
              src={getMediaItemUrl(znglyInfo?.headerLogo, MediaItemSizeEnum.MediumLarge) ?? ''}
            />
          </HeaderLogoLink>
        </Toolbar>
        <Divider />

        <List>
          {/* login button */}
          {!user && (
            <ListItem>
              <ListItemButton
                component="button"
                className={`z-btn-6 green ${styles.mButton}`}
                onClick={() => {
                  navigate({
                    to: '/auth/login',
                  });
                }}
              >
                Log In
              </ListItemButton>
            </ListItem>
          )}

          {/* CTA Buttons */}
          <HeaderButtons type="mobile" znglyInfo={znglyInfo} />
        </List>
      </Stack>
      <Stack>
        {/* Social Icons */}
        {!!znglyInfo?.customSocialLinks?.length && (
          <div className={styles.socialLinks} style={{ marginBottom: '0.5em' }}>
            {znglyInfo.customSocialLinks.map((link) => {
              const icon = faParseIconName(link?.name, 'fab');
              if (!icon) return <></>;
              return (
                <a key={`${link?.name}_${link?.url}`} href={link?.url ?? ''} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={[icon.pkg, icon.label]} />
                </a>
              );
            })}
          </div>
        )}
        <Divider />
        <List>
          <ListItem component={TsrLink} to="/privacy-policy">
            <ListItemIcon className={styles.mListItemIcon}>{<PolicyIcon />}</ListItemIcon>
            <ListItemText className={styles.mListItemText} secondary={'Privacy Policy'} />
          </ListItem>
        </List>
        <Divider />
        <List>
          <ListItem component={'a'} href="https://zngly.com/" target="_blank">
            <ListItemIcon className={styles.mListItemIcon}>{<CopyrightIcon />}</ListItemIcon>
            <ListItemText className={styles.mListItemText} primary={'Powered By Zngly'} />
          </ListItem>
        </List>
      </Stack>
    </Stack>
  );
};

const Header = ({ style, znglyInfo }: Props) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = useAuthStore((state) => state.user.user);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  return (
    <Box sx={{ flexGrow: 1 }} style={style}>
      <AppBar position="fixed" sx={{ backgroundColor: 'var(--header-bg-col)', color: 'var(--header-p-text-col)' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <IconButton
              color="inherit"
              size="large"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <HeaderLogoLink homeOnly znglyInfo={znglyInfo} style={{ display: 'flex' }}>
              <Box
                component="img"
                sx={{ height: 24, mr: 2, pointerEvents: 'cursor' }}
                src={getMediaItemUrl(znglyInfo?.headerLogo, MediaItemSizeEnum.MediumLarge) ?? ''}
              />
            </HeaderLogoLink>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            {!!user && <HeaderDDMenu />}
          </Stack>
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* second toolbar to not have content hidden underneath the appbar */}
      {/* Left Drawer */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { xs: 0 } }} aria-label="Side Drawer">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          <DrawerContent znglyInfo={znglyInfo} />
        </Drawer>
      </Box>
    </Box>
  );
};

type HeaderInputProps = {
  style?: React.CSSProperties;
};

const HeaderFallBackMobile = ({ style }: HeaderInputProps) => {
  const Skelet = ({ ...props }) => <Skeleton {...props} sx={{ backgroundColor: 'var(--header-p-text-col)' }} />;

  return (
    <Box sx={{ flexGrow: 1, marginBottom: '1em', ...style }}>
      <AppBar position="fixed" sx={{ backgroundColor: 'var(--header-bg-col)' }}>
        <Toolbar sx={{ textAlign: 'center' }}>
          <IconButton color="inherit" size="large" aria-label="open drawer" edge="start" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <Skelet />
          </Typography>

          <IconButton size="large" color="inherit" aria-label="account of current user">
            <AccountCircle />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Toolbar />
    </Box>
  );
};

const Wrapper = (props: HeaderInputProps) => {
  const ctx = useZnglyContext();
  const data = useFragment<Header_ZnglyInfo_Desktop$key>(Header_ZnglyInfo_Desktop, ctx.appData.queryRef?.znglyInfo);

  return <Header {...props} znglyInfo={data?.znglyInfo as Header_ZnglyInfo_Desktop$data['znglyInfo']} />;
};

export const HeaderMobile = (props: HeaderInputProps) => {
  const ctx = useZnglyContext();

  return (
    <Suspense fallback={<HeaderFallBackMobile style={props.style} />} exists={ctx?.appData?.queryRef?.znglyInfo}>
      {!!ctx?.appData?.queryRef && <Wrapper {...props} />}
    </Suspense>
  );
};
